'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { trackEvent } from '@/lib/analytics';
import { deliverForm, formEndpoints } from '@/lib/forms';
import { company } from '@/lib/company';

const contactSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email'),
  message: z.string().min(10, 'Message must be at least 10 characters'),
});

type ContactFormData = z.infer<typeof contactSchema>;

export function ContactForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [channel, setChannel] = useState<'mailto' | 'endpoint'>('mailto');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
  });

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Delivers via the visitor's mail client unless this deployment has an
      // endpoint explicitly configured. It never posts to the DataCode
      // backend — see src/lib/forms.ts.
      const result = await deliverForm({
        endpoint: formEndpoints.contact,
        subject: `Enquiry from ${data.name}`,
        fields: [
          ['Name', data.name],
          ['Email', data.email],
          ['Message', data.message],
        ],
        payload: data,
      });

      setChannel(result.channel);

      if (result.ok) {
        setSubmitStatus('success');
        trackEvent('contact_submit', { status: 'success', channel: result.channel });
        reset();
      } else {
        setSubmitStatus('error');
        trackEvent('contact_submit', { status: 'error', channel: result.channel });
      }
    } catch {
      setSubmitStatus('error');
      trackEvent('contact_submit', { status: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputStyle = {
    width: '100%',
    backgroundColor: 'transparent',
    borderTop: 'none',
    borderLeft: 'none',
    borderRight: 'none',
    borderBottom: '1px solid rgba(255,255,255,0.15)',
    paddingTop: '1.25rem',
    paddingBottom: '1.25rem',
    fontSize: 'clamp(1rem, 1.5vw, 1.25rem)',
    color: '#ffffff',
    outline: 'none',
    transition: 'border-color 0.3s',
    fontFamily: 'inherit',
  };

  const labelStyle = {
    fontSize: '10px',
    letterSpacing: '0.3em',
    textTransform: 'uppercase' as const,
    color: 'rgba(255,255,255,0.9)',
    marginBottom: '0.25rem',
    display: 'block',
  };

  return (
    <div style={{ width: '100%' }}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '2rem' }}
      >
        {/* Name */}
        <div>
          <label htmlFor="name" style={labelStyle}>Name</label>
          <input
            {...register('name')}
            type="text"
            id="name"
            placeholder="Your full name"
            style={{
              ...inputStyle,
              borderBottomColor: errors.name ? '#ef4444' : 'rgba(255,255,255,0.15)',
            }}
            onFocus={(e) => { if (!errors.name) e.currentTarget.style.borderBottomColor = 'rgba(255,255,255,0.5)'; }}
            onBlur={(e) => { if (!errors.name) e.currentTarget.style.borderBottomColor = 'rgba(255,255,255,0.15)'; }}
          />
          {errors.name && (
            <p style={{ fontSize: '0.75rem', color: '#ef4444', marginTop: '0.5rem' }}>{errors.name.message}</p>
          )}
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email" style={labelStyle}>Email</label>
          <input
            {...register('email')}
            type="email"
            id="email"
            placeholder="your@email.com"
            style={{
              ...inputStyle,
              borderBottomColor: errors.email ? '#ef4444' : 'rgba(255,255,255,0.15)',
            }}
            onFocus={(e) => { if (!errors.email) e.currentTarget.style.borderBottomColor = 'rgba(255,255,255,0.5)'; }}
            onBlur={(e) => { if (!errors.email) e.currentTarget.style.borderBottomColor = 'rgba(255,255,255,0.15)'; }}
          />
          {errors.email && (
            <p style={{ fontSize: '0.75rem', color: '#ef4444', marginTop: '0.5rem' }}>{errors.email.message}</p>
          )}
        </div>

        {/* Message */}
        <div>
          <label htmlFor="message" style={labelStyle}>Message</label>
          <textarea
            {...register('message')}
            id="message"
            rows={5}
            placeholder="Tell us about your project, or ask about the products..."
            style={{
              ...inputStyle,
              resize: 'none',
              borderBottomColor: errors.message ? '#ef4444' : 'rgba(255,255,255,0.15)',
            }}
            onFocus={(e) => { if (!errors.message) e.currentTarget.style.borderBottomColor = 'rgba(255,255,255,0.5)'; }}
            onBlur={(e) => { if (!errors.message) e.currentTarget.style.borderBottomColor = 'rgba(255,255,255,0.15)'; }}
          />
          {errors.message && (
            <p style={{ fontSize: '0.75rem', color: '#ef4444', marginTop: '0.5rem' }}>{errors.message.message}</p>
          )}
        </div>

        {/* Submit Button */}
        <div style={{ marginTop: '1rem' }}>
          <button
            type="submit"
            disabled={isSubmitting}
            className="group"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '1rem',
              backgroundColor: '#ffffff',
              color: '#0c0c0c',
              border: 'none',
              borderRadius: '100px',
              padding: '1.125rem 2.5rem',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              opacity: isSubmitting ? 0.5 : 1,
              transition: 'all 0.3s ease',
              width: '100%',
              fontFamily: 'inherit',
            }}
            onMouseEnter={(e) => { if (!isSubmitting) e.currentTarget.style.backgroundColor = '#e5e5e5'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#ffffff'; }}
          >
            <span
              style={{
                fontSize: '0.8125rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.2em',
              }}
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </span>
            <div
              style={{
                width: '2.5rem',
                height: '2.5rem',
                borderRadius: '50%',
                backgroundColor: '#0c0c0c',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
              }}
              className="group-hover:rotate-45"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </div>
          </button>
        </div>

        {/* Status Messages */}
        {submitStatus === 'success' && (
          <div
            style={{
              textAlign: 'center',
              padding: '1rem',
              borderRadius: '8px',
              backgroundColor: 'rgba(34, 197, 94, 0.1)',
              border: '1px solid rgba(34, 197, 94, 0.2)',
            }}
          >
            <p style={{ color: '#22c55e', fontSize: '0.8125rem', textTransform: 'uppercase', letterSpacing: '0.15em' }}>
              {channel === 'mailto'
                ? `Your email app should now be open with the message ready to send to ${company.email}.`
                : 'Thank you. Your message has been sent.'}
            </p>
          </div>
        )}
        {submitStatus === 'error' && (
          <div
            style={{
              textAlign: 'center',
              padding: '1rem',
              borderRadius: '8px',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.2)',
            }}
          >
            <p style={{ color: '#ef4444', fontSize: '0.8125rem', textTransform: 'uppercase', letterSpacing: '0.15em' }}>
              {`Something went wrong. Please email us directly at ${company.email}.`}
            </p>
          </div>
        )}
      </form>
    </div>
  );
}
